import { useEffect, useState } from "react";
import { useProfileStore, getFacilityKeyForPortal, getPatientKey, type FacilityInfo } from "@/store/profile";
import patientRequest from "@/api/patient-request";
import authRequest from "@/api/auth-request";
import { formatDateTime } from "@/utils/format";
import { DashTask } from "@/components/dashboard/DashTask";

function buildMapsUrl(info: FacilityInfo): string {
  const parts = [info.address, info.address2, info.city, info.state, info.zip]
    .filter(Boolean)
    .join(", ");
  return `https://maps.google.com/?q=${encodeURIComponent(parts)}`;
}

function formatAddress(info: FacilityInfo): string {
  const line1 = [info.address, info.address2].filter(Boolean).join(", ");
  const line2 = [info.city, info.state].filter(Boolean).join(", ");
  return [line1, `${line2} ${info.zip}`.trim()].filter(Boolean).join(", ");
}

function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

export function DashboardPage() {
  const currentPatient = useProfileStore((s) => s.currentPatient);
  const [taskCount, setTaskCount] = useState<number | null>(null);
  const [nextAppt, setNextAppt] = useState<string | null>(null);
  const [facility, setFacility] = useState<FacilityInfo | null>(null);

  useEffect(() => {
    if (!currentPatient) return;
    const facilityKey = getFacilityKeyForPortal();
    const patientKey = getPatientKey();
    if (!facilityKey || !patientKey) return;
    let cancelled = false;
    patientRequest.request
      .getpatientfutureappts(facilityKey, patientKey)
      .then((res) => {
        if (cancelled) return;
        const data = (res?.data as string[] | undefined) ?? [];
        const sorted = [...data].sort(
          (a, b) => new Date(a).getTime() - new Date(b).getTime()
        );
        setNextAppt(sorted[0] ?? null);
      })
      .catch(() => setNextAppt(null));
    return () => {
      cancelled = true;
    };
  }, [currentPatient]);

  useEffect(() => {
    if (!currentPatient) return;
    const key = getFacilityKeyForPortal();
    if (!key) return;
    let cancelled = false;
    authRequest.request
      .getFacilityInfo(key)
      .then((res) => {
        if (!cancelled && res?.data) setFacility(res.data as FacilityInfo);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [currentPatient]);

  return (
    <div className="flex flex-col gap-5">
      <section className="flex flex-col gap-3">
        <div className="flex items-center">
          <div
            className="mr-3 h-7 w-1 rounded-sm bg-healthsource-navy"
          />
          <h2 className="text-xl font-extrabold text-healthsource-dark">
            Your Action Items
          </h2>
          {taskCount !== null && taskCount > 0 && (
            <span
              className="ml-2.5 rounded-xl bg-[var(--hs-tint)] px-2.5 py-0.5 text-xs font-bold text-healthsource-navy"
            >
              {taskCount}
            </span>
          )}
        </div>
        <div
          className="sm:overflow-hidden sm:rounded-xl sm:border sm:border-healthsource-lightLavender sm:bg-white sm:shadow-[0_2px_8px_rgba(30,37,43,0.06)]"
        >
          <DashTask onTaskCountChange={setTaskCount} />
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex items-center">
          <div
            className="mr-3 h-7 w-1 rounded-sm bg-healthsource-navy"
          />
          <h2 className="text-xl font-extrabold text-healthsource-dark">
            Your Next Appointment
          </h2>
        </div>
        <div
          className="sm:overflow-hidden sm:rounded-xl sm:border sm:border-healthsource-lightLavender sm:bg-white sm:shadow-[0_2px_8px_rgba(30,37,43,0.06)]"
        >
          <ul className="flex flex-col gap-3 sm:gap-3.5 sm:p-5">
            <li className="flex overflow-hidden rounded-lg border border-healthsource-lightLavender bg-white transition-shadow hover:shadow-md"
              style={{ boxShadow: "0 2px 8px rgba(30,37,43,0.06)" }}
            >
              <div
                className="w-1 flex-shrink-0 rounded-r-sm bg-healthsource-navy"
              />
              <div className="min-w-0 flex-1 px-4 py-4 sm:pl-5">
                <p className="text-[11px] font-semibold text-healthsource-gray">
                  Next Appointment
                </p>
                <div className="mt-0.5 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[13px] font-bold text-healthsource-dark">
                  <span>
                    {nextAppt ? formatDateTime(nextAppt) : "None scheduled"}
                  </span>
                  {facility?.address && (
                    <>
                      <span className="text-healthsource-gray" aria-hidden>|</span>
                      <a
                        href={buildMapsUrl(facility)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-healthsource-navy underline decoration-healthsource-navy/30 underline-offset-2 transition-colors hover:decoration-healthsource-navy"
                      >
                        {formatAddress(facility)}
                      </a>
                    </>
                  )}
                  {facility?.phone && (
                    <>
                      <span className="text-healthsource-gray" aria-hidden>|</span>
                      <a
                        href={`tel:${facility.phone.replace(/\D/g, "")}`}
                        className="text-healthsource-navy underline decoration-healthsource-navy/30 underline-offset-2 transition-colors hover:decoration-healthsource-navy"
                      >
                        {formatPhone(facility.phone)}
                      </a>
                    </>
                  )}
                </div>
              </div>
            </li>
          </ul>
        </div>
      </section>
    </div>
  );
}
